import { TypedDocumentNode, gql } from '@apollo/client';
import { faClock } from '@fortawesome/pro-solid-svg-icons';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes';
import { LoadingButton } from '@sorare/core/src/atoms/buttons/LoadingButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { HeadlineM, LabelM, LabelS } from '@sorare/core/src/atoms/typography';
import { Block } from '@sorare/core/src/components/rivals/branded/Block';
import { Progress } from '@sorare/core/src/components/rivals/branded/Progress';
import { ShortTimeLeft } from '@sorare/core/src/contexts/ticker/ShortTimeLeft';
import { glossary } from '@sorare/core/src/lib/glossary';
import { HexColor } from '@sorare/core/src/style/types';

import { RewardBoxDialogWithOnboarding } from '@sorare/gameplay/src/components/rewards/RewardBoxDialogWithOnboarding';

import { useClaimMissionTask } from 'components/rivals/MissionsPage/DailyChallenges/useClaimMissionTask';
import { RivalsRewardPreview } from 'components/rivals/RivalsRewardPreview';

import { ManagerProgressionMissionContentTile_managerProgressionMissionContentTile } from './__generated__/index.graphql';

const Content = styled(SBHorizontal)`
  width: 100%;
  align-items: flex-end;
`;

type Props = {
  tile: ManagerProgressionMissionContentTile_managerProgressionMissionContentTile;
};
export const ManagerProgressionMissionContentTile = ({ tile }: Props) => {
  const [claimTask, { loading }] = useClaimMissionTask();
  const [rewardDialogOpened, setRewardDialogOpened] = useState(false);

  const { title, color, mission } = tile;
  const { progress, target } = mission;

  const onClaim = async () => {
    await claimTask({ variables: { name: mission.name } });
    setRewardDialogOpened(true);
  };
  const completed = mission.aasmState === ManagerProgressionTaskState.COMPLETED;
  const claimed = mission.aasmState === ManagerProgressionTaskState.CLAIMED;
  const probabilisticBundle = mission.probabilisticBundles.at(0);
  const reward = mission.configRewards.at(0);

  return (
    <>
      <Block
        color={`#${color}` as HexColor}
        title={
          <LabelS color="var(--c-neutral-100)" bold>
            <FormattedMessage
              id="ManagerProgressionMissionContentTile.title"
              defaultMessage="Mission"
            />
          </LabelS>
        }
        subtitle={
          title && <HeadlineM color="var(--c-neutral-100)">{title}</HeadlineM>
        }
        extra={
          typeof progress === 'number' && progress > 0 ? (
            <Progress
              progress={progress}
              target={target}
              color="var(--c-rivals-friendlies)"
              activeColor="var(--c-neutral-100)"
            />
          ) : (
            mission.cutoffAt && (
              <LabelM color="var(--c-neutral-400)" bold>
                <Horizontal gap={0.5}>
                  <FontAwesomeIcon icon={faClock} />
                  <ShortTimeLeft time={mission.cutoffAt} />
                </Horizontal>
              </LabelM>
            )
          )
        }
      >
        <Content gap={3}>
          {completed || claimed ? (
            <LoadingButton
              size="medium"
              color="secondary"
              onClick={onClaim}
              disabled={claimed}
              loading={loading}
            >
              {claimed ? (
                <FormattedMessage {...glossary.claimed} />
              ) : (
                <FormattedMessage {...glossary.claim} />
              )}
            </LoadingButton>
          ) : (
            <LabelM>{mission.description}</LabelM>
          )}
          <Vertical center>
            <LabelM bold>
              <FormattedMessage {...glossary.reward} />
            </LabelM>
            {reward && <RivalsRewardPreview reward={reward} />}
          </Vertical>
        </Content>
      </Block>
      {probabilisticBundle && rewardDialogOpened && (
        <RewardBoxDialogWithOnboarding
          open
          onClose={() => {
            setRewardDialogOpened(false);
          }}
          bundle={probabilisticBundle}
        />
      )}
    </>
  );
};
ManagerProgressionMissionContentTile.fragments = {
  managerProgressionMissionContentTile: gql`
    fragment ManagerProgressionMissionContentTile_managerProgressionMissionContentTile on ManagerProgressionMissionContentTile {
      id
      color
      headline
      title
      type
      mission {
        id
        aasmState
        title
        description
        name
        cutoffAt
        target
        progress
        configRewards {
          ...RivalsRewardPreview_managerTaskRewardInterface
        }
        probabilisticBundles {
          id
          ...RewardBoxDialogWithOnboarding_probabilisticBundle
        }
      }
    }
    ${RewardBoxDialogWithOnboarding.fragments.probabilisticBundle}
    ${RivalsRewardPreview.fragments.managerTaskRewardInterface}
  ` as TypedDocumentNode<ManagerProgressionMissionContentTile_managerProgressionMissionContentTile>,
};
